// Client-side helpers around the universities + student verification +
// university_requests schema introduced in migration student_verification.
//
// Read helpers swallow errors and return [] / null — these power the
// signup typeahead + admin review queue and should never block the UI.
// Write helpers throw with the RPC's error message so call-sites can
// surface human-readable text in toasts / banners.

import { supabase } from "./supabase";

export type University = {
  id: string;
  name: string;
  primary_email_domain: string | null;
  alt_email_domains: string[];
  is_active: boolean;
};

export type StudentVerificationRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  university: string | null;
  university_id: string | null;
  university_name: string | null;
  school_verified: boolean;
  school_verification_method: "email_domain" | "manual_admin" | "self_attested" | null;
  school_verified_at: string | null;
  created_at: string;
};

export type UniversityRequest = {
  id: string;
  requested_by: string;
  school_name: string;
  primary_domain: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
  status: "pending" | "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_reason: string | null;
  resolved_university_id: string | null;
  created_at: string;
  requester_email?: string | null;
  requester_name?: string | null;
};

// ─── Reads ────────────────────────────────────────────────────────────────

export async function listUniversities(): Promise<University[]> {
  const { data, error } = await supabase
    .from("universities")
    .select("id, name, primary_email_domain, alt_email_domains, is_active")
    .eq("is_active", true)
    .order("name", { ascending: true });
  if (error || !data) return [];
  return data as University[];
}

// ─── Admin school-directory reads (Prompt 7) ─────────────────────────────

export type DirectoryUniversity = {
  id: string;
  name: string;
  primary_email_domain: string | null;
  alt_email_domains: string[];
  state_code: string | null;
  country_code: string | null;
  website_url: string | null;
  is_active: boolean;
};

export type DirectoryFilters = {
  search?: string;
  state?: string | null;
  isActive?: boolean | null;
  hasEmailDomain?: boolean | null;
};

export type DirectoryPage = {
  rows: DirectoryUniversity[];
  total: number;
};

const DIRECTORY_PAGE_SIZE = 50;

/** Paginated, filterable list of universities for /admin/students/review's
 *  School directory tab. Defaults to 50 rows per page. Filters compose;
 *  passing `null` for a filter means "any". */
export async function listUniversityDirectory(
  page: number,
  filters: DirectoryFilters,
): Promise<DirectoryPage> {
  const from = page * DIRECTORY_PAGE_SIZE;
  const to = from + DIRECTORY_PAGE_SIZE - 1;
  let q = supabase
    .from("universities")
    .select(
      "id, name, primary_email_domain, alt_email_domains, state_code, country_code, website_url, is_active",
      { count: "exact" },
    )
    .order("name", { ascending: true })
    .range(from, to);

  if (filters.state) q = q.eq("state_code", filters.state);
  if (filters.isActive === true) q = q.eq("is_active", true);
  if (filters.isActive === false) q = q.eq("is_active", false);
  if (filters.hasEmailDomain === true) q = q.not("primary_email_domain", "is", null);
  if (filters.hasEmailDomain === false) q = q.is("primary_email_domain", null);
  if (filters.search && filters.search.trim().length > 0) {
    const s = filters.search.trim().replace(/[%_]/g, "");
    // Match either name or primary domain (case-insensitive). Two
    // separate ilike clauses joined via PostgREST's `or` syntax.
    q = q.or(`name.ilike.%${s}%,primary_email_domain.ilike.%${s}%`);
  }

  const { data, count, error } = await q;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as DirectoryUniversity[], total: count ?? 0 };
}

/** Per-state active counts for the "top 10 states" snippet at the top of
 *  the directory. Returns sorted by count desc, plus a NULL row for rows
 *  without a state_code. */
export async function listUniversityStateBreakdown(): Promise<Array<{ state_code: string | null; n: number }>> {
  // PostgREST doesn't expose GROUP BY directly; we use the `count` head
  // pattern looping a small list of states. Since the universities table
  // is only a few thousand rows, paginating-and-grouping client-side is
  // fine.
  const { data, error } = await supabase
    .from("universities")
    .select("state_code")
    .eq("is_active", true)
    .eq("country_code", "US");
  if (error || !data) return [];
  const counts = new Map<string | null, number>();
  for (const row of data as Array<{ state_code: string | null }>) {
    const k = (row.state_code as string | null) ?? null;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([state_code, n]) => ({ state_code, n }))
    .sort((a, b) => b.n - a.n);
}

// ─── Admin school-directory writes ───────────────────────────────────────

export async function updateUniversityRow(
  id: string,
  patch: Partial<{
    name: string;
    primary_email_domain: string | null;
    alt_email_domains: string[];
    state_code: string | null;
    country_code: string | null;
    website_url: string | null;
    is_active: boolean;
  }>,
): Promise<void> {
  const { error } = await supabase.from("universities").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deactivateUniversity(id: string): Promise<void> {
  return updateUniversityRow(id, { is_active: false });
}

export async function reactivateUniversity(id: string): Promise<void> {
  return updateUniversityRow(id, { is_active: true });
}

/**
 * Merge `sourceId`'s domains into `targetId`'s alt_email_domains and
 * deactivate the source. Pure UI helper — does NOT migrate students/etc.
 * (Any student with university_id = sourceId will keep that FK; an
 * admin can re-resolve via the existing verify flow if needed.)
 */
export async function mergeUniversityInto(sourceId: string, targetId: string): Promise<void> {
  const { data: rows, error } = await supabase
    .from("universities")
    .select("id, primary_email_domain, alt_email_domains")
    .in("id", [sourceId, targetId]);
  if (error || !rows || rows.length !== 2) {
    throw new Error("Merge: could not load both rows");
  }
  const source = rows.find((r) => r.id === sourceId);
  const target = rows.find((r) => r.id === targetId);
  if (!source || !target) throw new Error("Merge: source/target mismatch");

  const sourceDomains = [
    ...(source.primary_email_domain ? [source.primary_email_domain as string] : []),
    ...((source.alt_email_domains as string[] | null) ?? []),
  ];
  const targetAlts = new Set<string>((target.alt_email_domains as string[] | null) ?? []);
  // Don't add the target's primary back to its own alts.
  const targetPrimary = ((target.primary_email_domain as string | null) ?? "").toLowerCase();
  for (const d of sourceDomains) {
    if (d.toLowerCase() !== targetPrimary) targetAlts.add(d);
  }
  const merged = Array.from(targetAlts);

  // Two writes: update target alts, then deactivate source. Best-effort —
  // if the second write fails we surface but the merge already partly
  // happened. RLS scopes these to admins so failures are operator-side.
  await updateUniversityRow(targetId, { alt_email_domains: merged });
  await updateUniversityRow(sourceId, { is_active: false });
}

/** Bulk re-activate every is_active=false row in a given state. Returns
 *  the count of rows changed. */
export async function bulkReactivateByState(state: string): Promise<number> {
  const { data, error } = await supabase
    .from("universities")
    .update({ is_active: true })
    .eq("state_code", state)
    .eq("is_active", false)
    .select("id");
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

/** Resolve a university UUID from an email's domain. Public RPC — works
 *  pre-auth, used by the signup form to preview the matched school. */
export async function resolveUniversityByEmail(email: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("resolve_university_by_email", { p_email: email });
  if (error) return null;
  return typeof data === "string" ? data : null;
}

/** Admin review queue: students that need attention. Pulled from students +
 *  joined user_profiles (for display name + email) + universities (for
 *  resolved school name). Caller filters client-side by tab. */
export async function listStudentsForReview(): Promise<StudentVerificationRow[]> {
  // Two-query pattern (FK ambiguity / RLS on user_profiles makes the
  // PostgREST embed flaky). Pull students first, then hydrate profile +
  // university name in batches.
  const { data: students, error } = await supabase
    .from("students")
    .select("id, university, university_id, school_verified, school_verification_method, school_verified_at, created_at")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error || !students) return [];

  const ids = students.map((s) => s.id as string);
  const uniIds = Array.from(new Set(students.map((s) => s.university_id as string | null).filter((v): v is string => !!v)));

  const [{ data: profiles }, { data: unis }] = await Promise.all([
    ids.length === 0
      ? Promise.resolve({ data: [] as { id: string; email: string | null; full_name: string | null }[] })
      : supabase.from("user_profiles").select("id, email, full_name").in("id", ids),
    uniIds.length === 0
      ? Promise.resolve({ data: [] as { id: string; name: string }[] })
      : supabase.from("universities").select("id, name").in("id", uniIds),
  ]);

  const profileById = new Map((profiles ?? []).map((p) => [p.id as string, p as { email: string | null; full_name: string | null }]));
  const uniNameById = new Map((unis ?? []).map((u) => [u.id as string, u.name as string]));

  return students.map((s) => {
    const p = profileById.get(s.id as string);
    const uniId = (s.university_id as string | null) ?? null;
    return {
      id: s.id as string,
      email: p?.email ?? null,
      full_name: p?.full_name ?? null,
      university: (s.university as string | null) ?? null,
      university_id: uniId,
      university_name: uniId ? uniNameById.get(uniId) ?? null : null,
      school_verified: s.school_verified as boolean,
      school_verification_method: (s.school_verification_method as StudentVerificationRow["school_verification_method"]) ?? null,
      school_verified_at: (s.school_verified_at as string | null) ?? null,
      created_at: s.created_at as string,
    };
  });
}

export async function listUniversityRequests(): Promise<UniversityRequest[]> {
  const { data, error } = await supabase
    .from("university_requests")
    .select("*")
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  const rows = data as UniversityRequest[];

  const ids = Array.from(new Set(rows.map((r) => r.requested_by).filter((v): v is string => !!v)));
  if (ids.length === 0) return rows;
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, email, full_name")
    .in("id", ids);
  const byId = new Map((profiles ?? []).map((p) => [p.id as string, p as { email: string | null; full_name: string | null }]));
  return rows.map((r) => ({
    ...r,
    requester_email: byId.get(r.requested_by)?.email ?? null,
    requester_name: byId.get(r.requested_by)?.full_name ?? null,
  }));
}

// ─── Writes ───────────────────────────────────────────────────────────────

export async function adminVerifyStudentSchool(studentId: string, universityId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_verify_student_school", {
    p_student_id: studentId,
    p_university_id: universityId,
  });
  if (error) throw new Error(error.message);
}

export async function adminRejectStudentSchool(studentId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("admin_reject_student_school", {
    p_student_id: studentId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

/** Student self-attests their school after an unmatched email. Writes
 *  university_id + university (string) on their own students row. */
export async function setStudentSchoolSelfAttested(universityId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_set_student_school_self_attested", {
    p_university_id: universityId,
  });
  if (error) throw new Error(error.message);
}

export async function requestUniversity(input: {
  school_name: string;
  primary_domain?: string | null;
  city?: string | null;
  state?: string | null;
  notes?: string | null;
}): Promise<void> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) throw new Error("Not signed in");
  const { error } = await supabase.from("university_requests").insert({
    requested_by: userData.user.id,
    school_name: input.school_name.trim(),
    primary_domain: input.primary_domain?.trim() || null,
    city: input.city?.trim() || null,
    state: input.state?.trim() || null,
    notes: input.notes?.trim() || null,
  });
  if (error) throw new Error(error.message);
}

export async function approveUniversityRequest(
  requestId: string,
  primaryDomain?: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc("approve_university_request", {
    p_request_id: requestId,
    p_primary_domain: primaryDomain ?? null,
    p_alt_domains: null,
  });
  if (error) throw new Error(error.message);
  if (typeof data !== "string") throw new Error("approve_university_request did not return a UUID");
  return data;
}

export async function rejectUniversityRequest(requestId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("reject_university_request", {
    p_request_id: requestId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

/** Server-enforced join: routes through the register_for_competition RPC
 *  which validates all eligibility + verification gates and raises with a
 *  human-readable message on rejection. */
export async function registerForCompetitionRpc(competitionId: string): Promise<string> {
  const { data, error } = await supabase.rpc("register_for_competition", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
  if (typeof data !== "string") throw new Error("register_for_competition did not return a UUID");
  return data;
}
